import type { DeviceSpecDevice, DeviceSpecTag } from '../../types'
import { channelDef } from './catalog'
import { evaluateFormula } from './formula'
import { normalizeAddress } from './model'

// 엑셀처럼 칸에 수식을 남긴다 — tag.formulas[칸] = "={code}_PV", tag[칸] = 계산 결과.
// 명세서가 바뀔 때마다(useSpecDraft) 수식이 있는 디바이스를 다시 계산하므로, 설비구분·디바이스 코드를 바꾸거나
// 위 칸이 바뀌면(=↑+1) 값이 따라 바뀐다. Edge 로는 값만 간다.

export const FORMULA_FIELDS = ['address', 'size', 'description', 'alias'] as const
export type FormulaField = typeof FORMULA_FIELDS[number]

export interface CellError { key: string; field: FormulaField; row: number; message: string }

const hasFormulas = (t: DeviceSpecTag) => !!t.formulas && Object.keys(t.formulas).length > 0

/** 칸에 보이는/편집할 원문 — 수식이 있으면 수식, 없으면 값 */
export const cellSource = (tag: DeviceSpecTag, field: FormulaField) => tag.formulas?.[field] ?? tag[field] ?? ''

/** 칸에 입력한 글자를 반영 — '=' 로 시작하면 수식으로 남기고(값은 재계산), 아니면 값으로 넣고 수식을 지운다 */
export function setCellInput(tag: DeviceSpecTag, field: FormulaField, text: string, parse: (v: string) => string): DeviceSpecTag {
  const v = text.trim()
  if (v.startsWith('=')) return { ...tag, formulas: { ...tag.formulas, [field]: v } }
  const rest = { ...tag.formulas }
  delete rest[field]
  return { ...tag, [field]: parse(text), formulas: rest }
}

/** 디바이스의 수식 칸을 위에서부터 다시 계산 (=↑ 는 계산된 위 칸 값을 본다). 바뀐 게 없으면 같은 객체 */
export function recomputeDevice(device: DeviceSpecDevice): { device: DeviceSpecDevice; errors: CellError[] } {
  if (!device.tags.some(hasFormulas)) return { device, errors: [] }
  const def = channelDef(device.channelType)
  const errors: CellError[] = []
  let changed = false
  const out: DeviceSpecTag[] = []
  device.tags.forEach((t, row) => {
    let tag = t
    for (const field of FORMULA_FIELDS) {
      const formula = t.formulas?.[field]
      if (!formula) continue
      const r = evaluateFormula(formula, { row: tag, prev: out[row - 1], rowIndex: row, field, device })
      if (!r.ok) errors.push({ key: t.key, field, row, message: r.error })
      const value = r.ok ? (field === 'address' ? normalizeAddress(def, r.value) : r.value) : ''
      if (tag[field] !== value) tag = { ...tag, [field]: value }
    }
    if (tag !== t) changed = true
    out.push(tag)
  })
  return { device: changed ? { ...device, tags: out } : device, errors }
}

export function recomputeDevices(devices: DeviceSpecDevice[]): DeviceSpecDevice[] {
  let changed = false
  const next = devices.map(d => {
    const r = recomputeDevice(d).device
    if (r !== d) changed = true
    return r
  })
  return changed ? next : devices
}
